import { Injectable, OnDestroy } from '@angular/core';
import { Params } from '@angular/router';
import {
  GetPublicStoreThemeResponse,
  PublicStoreTheme,
  PublicStoreThemeInterface,
  StoreService,
} from '@vendasta/marketplace-packages';
import { StorefrontService } from '@vendasta/storefront';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';

const themingDefaults: PublicStoreThemeInterface = {
  backgroundColor: '#FF', //transparent
  logoBarColor: 'white',
  accentColor: '#3c78d8',
  primaryFontColor: '#212121',
  secondaryFontColor: '#616161',
  primaryCategoryColor: '#e5e5e5',
  secondaryCategoryColor: '#e0e0e0',
};

@Injectable({ providedIn: 'root' })
export class ThemingService implements OnDestroy {
  partnerId$: Observable<string>;
  marketId$: Observable<string>;
  private readonly ieThemingId = 'public-store-theming-variables';
  private unsubscribe$$ = new Subject<void>();

  constructor(private storeService: StoreService, private storefrontService: StorefrontService) {}

  public initializeTheme(queryParams$: Observable<Params>): void {
    this.marketId$ = this.storefrontService.marketId$;
    this.partnerId$ = this.storefrontService.partnerId$.pipe(filter((partnerId) => !!partnerId));
    combineLatest([this.partnerId$, this.marketId$])
      .pipe(
        takeUntil(this.unsubscribe$$),
        switchMap(([partnerId, marketId]) => {
          return this.storeService.getPublicStoreTheme(partnerId, marketId ? marketId : 'default').pipe(
            catchError((err) => {
              console.error(err.error.message);
              return of({} as GetPublicStoreThemeResponse);
            }),
          );
        }),
        withLatestFrom(queryParams$),
        map(([response, queryParams]) => this.buildThemingCSS(response.publicStoreTheme, queryParams)),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.unsubscribe$$.next();
    this.unsubscribe$$.complete();
  }

  buildThemingCSS(theme: PublicStoreTheme, queryParams: Params): void {
    const css = `:root {
            --themingBackgroundColor: ${resolveColor(
              queryParams?.backgroundColor,
              theme?.backgroundColor,
              themingDefaults.backgroundColor,
            )};
            --themingLogoBarColor: ${resolveColor(
              queryParams?.logoBarColor,
              theme?.logoBarColor,
              themingDefaults.logoBarColor,
            )};
            --themingAccentColor: ${resolveColor(
              queryParams?.accentColor,
              theme?.accentColor,
              themingDefaults.accentColor,
            )};
            --themingPrimaryFontColor: ${resolveColor(
              queryParams?.primaryFontColor,
              theme?.primaryFontColor,
              themingDefaults.primaryFontColor,
            )};
            --themingSecondaryFontColor: ${resolveColor(
              queryParams?.secondaryFontColor,
              theme?.secondaryFontColor,
              themingDefaults.secondaryFontColor,
            )};
            --themingCategoryPrimaryColor: ${resolveColor(
              queryParams?.primaryCategoryColor,
              theme?.primaryCategoryColor,
              themingDefaults.primaryCategoryColor,
            )};
            --themingCategorySecondaryColor: ${resolveColor(
              queryParams?.secondaryCategoryColor,
              theme?.secondaryCategoryColor,
              themingDefaults.secondaryCategoryColor,
            )};
        }`;

    const existingStyle = document.getElementById(this.ieThemingId);
    if (existingStyle) {
      existingStyle.parentNode.removeChild(existingStyle);
    }

    const style = document.createElement('style');
    style.setAttribute('id', this.ieThemingId);
    document.head.appendChild(style);
    style.appendChild(document.createTextNode(css));
  }
}

/**
 * Returns the first color in the list of provided colors that is defined
 * @param colors - the list of colors, in order of precedence
 */
export function resolveColor(...colors: string[]): string {
  for (const c of colors) {
    if (c) {
      return c;
    }
  }
  return 'white';
}
